import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User } from "../models/User";
import { UserService } from "../services/UserService";
import { useAuth } from "./AuthContext";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { getSubdomainFromUrl } from "../utils/url";
import { useSocket } from "./SocketContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { Professional } from "../models/Professional";

interface Errors {
  current?: Error | null;
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  current?: boolean;
  list?: boolean;
  details?: boolean;
}

interface UserContextProps {
  currentUser: User | null;
  selectedUser: User | null;
  setSelectedUser: (userId: User["_id"]) => void;
  users: User[];
  loading: Loadings;
  error: Errors;
  createUser: (
    userData: Omit<
      User,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "status"
    >,
  ) => Promise<string>;
  updateUser: (
    userId: User["_id"],
    file: globalThis.File | null,
    updatedData: Partial<Omit<User, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  createProfessional: (
    userId: User["_id"],
    file: globalThis.File | null,
    updatedData: Partial<Omit<Professional, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteUserById: (userId: string) => Promise<void>;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<Errors>({ list: null, details: null });
  const [loading, setLoading] = useState<Loadings>({
    list: false,
    details: false,
  });

  useSocketEventByIds("users:changed", ["*"], (updatedDocument) => {
    if (updatedDocument._id === user?.uid) {
      setCurrentUser(updatedDocument);
    } else {
      fetchUsers(); // Or directly update state if you prefer
    }
  });

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!user) {
        setCurrentUser(null);
        setLoading({ ...loading, details: false });
        return;
      }
      setLoading({ ...loading, details: true });
      try {
        const userDetails = await UserService.getUser(user.uid);
        if (userDetails) {
          setCurrentUser(userDetails);
          setError((prevErrors) => ({ ...prevErrors, currentUser: null }));
        } else {
          setError((prevErrors) => ({
            ...prevErrors,
            currentUser: new Error("User not found"),
          }));
        }
      } catch (err) {
        console.error("Failed to fetch current user:", err);
        setError((prevErrors) => ({
          ...prevErrors,
          currentUser: new Error(
            "Failed to fetch current user. Please try again later.",
          ),
        }));
      } finally {
        setLoading({ ...loading, details: false });
      }
    };

    fetchCurrentUser();
  }, [user]);

  const fetchUsers = async () => {
    setLoading({ ...loading, list: true });
    try {
      const usersList = await UserService.getUsers();
      setUsers(usersList);
      setLoading({ ...loading, list: false });
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setLoading({ ...loading, list: false });
      // Set the error for the list fetch
      setError((prevErrors) => ({
        ...prevErrors,
        list: new Error("Failed to fetch users. Please try again later."),
      }));
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    fetchUsers(); // Initial fetch for users
  }, [currentUser]); // Empty dependency array to run only once on mount

  const createUser = async (
    userData: Omit<
      User,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "status"
    >,
  ) => {
    const newUserId = await UserService.createUser({
      ...userData,
    });
    return newUserId;
  };

  const updateUser = async (
    userId: string,
    file: globalThis.File | null,
    updatedData: Partial<Omit<User, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await UserService.updateUser(userId, file, updatedData);
  };

  const deleteUserById = async (userId: string) => {
    await UserService.deleteUser(userId);
    setUsers((prevUsers) => prevUsers.filter((user) => user._id !== userId));
  };

  const setSelectedUserById = async (userId: string) => {
    if (!user) return;
    if (loading.details) {
      return;
    } // Prevent multiple calls
    setLoading((prev) => ({ ...prev, details: true })); // Start loading state
    const selectedUser = users.find((p) => p._id === userId);
    if (selectedUser) {
      setSelectedUser(selectedUser);
    } else {
      const professional = await UserService.getUser(userId);
      setSelectedUser(professional);
    }
    setLoading((prev) => ({ ...prev, details: false })); // End loading state
  };

  const createProfessional = async (
    userId: string,
    file: globalThis.File | null,
    updatedData: Partial<Omit<Professional, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await UserService.createProfessional(userId, file, updatedData);
  };

  return (
    <UserContext.Provider
      value={{
        currentUser,
        users,
        updateUser,
        loading,
        error,
        selectedUser,
        setSelectedUser: setSelectedUserById,
        deleteUserById,
        createUser,
        createProfessional,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
